<template>
    <div class="personal-dashboard-popup" :class="{'margin-left-20 margin-right-20': viewSize.md || viewSize.mdUp}">
        <div id="personal-dashboard-popup-main" class="remove-margin">
            <div class="flex flex-wrap align-items-center gap-15">
                <div class="background-primary padding-5 flex-shrink">
                    <img src="/logo.png" :alt="layoutData.title" class="logo-width">
                </div>

                <person-box-user :id="currentUser.id" hide-contact class="full-width">
                    <template #name>
                        <div class="header-title" v-text="title"></div>
                    </template>
                </person-box-user>

                <div class="time-tracker flex--item-right">
                    <top-bar-time-tracker with-background from-dashboard/>
                </div>
            </div>

            <div class="spacer-5">
                <div class="text-center color-primary font-size-18 font-weight-bold margin-bottom-8">
                    <span>{{dateHelper('l d.m.Y')}}</span>
                </div>
            </div>

            <div class="spacer-5">
                <loading-indicator v-if="loading" text="Daten werden abgerufen, bitte haben Sie einen Moment Geduld..."></loading-indicator>
                <div v-else class="flex flex-wrap flex-row gap-15" :style="{'flex-direction: column': viewSize.smDown}">

                    <div key="global-news"
                        :class="['flex-grow flex-min-content', {'limited-width': viewSize.xlUp, 'widget-flex-mobile-wrapper': viewSize.smDown}]">
                        <div :class="['pd-container news-pd-container-color', {'no-margin': viewSize.smDown}]">
                            <div class="pd-container-header flex">
                                <div v-if="viewSize.lgDown" v-interact="() => scrollLeft()" class="no-select">
                                    <i class="material-icons pointer" aria-hidden="true" role="img">
                                        keyboard_arrow_left
                                    </i>
                                </div>
                                <div class="flex-grow flex-center">Wir bringen Sie auf den neuesten Stand</div>
                                <div v-if="viewSize.lgDown" v-interact="() => scrollRight()" class="no-select flex flex-align-right">
                                    <i class="material-icons pointer" aria-hidden="true" role="img">
                                        keyboard_arrow_right
                                    </i>
                                </div>
                            </div>

                            <div class="news-pd-container-body">
                                <div :class="{'flex gap-15 overflow-x-scroll full-max-width': viewSize.lgDown}" ref="newsContainer">
                                    <div v-for="news of globalNews" class="news-card" style="min-width: 300px;" :id="'news-card' + news.id">
                                        <div class="icon-container">
                                            <img :src="news.imagePath" :alt="news.title">
                                        </div>
                                        <div class="content">
                                            <a :href="news.link">
                                                <h3>{{news.title}}</h3>
                                            </a>
                                            <p>
                                                {{news.content_teaser}}
                                            </p>
                                            <a class="more-link" :href="news.link">[mehr]</a>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div v-for="listId in 3" :key="'user-list-' + (listId - 1)"
                        :class="['flex-grow flex-max-content', {'limited-width': viewSize.mdUp, 'widget-flex-mobile-wrapper': viewSize.smDown}]">
                        <div :class="['pd-container', {'no-margin': viewSize.smDown}]">
                            <div :class="['pd-container-header flex', {'cursor-default': !viewSize.smDown}]" v-interact="() => toggleContainer(listId)">
                                <div class="flex-grow">{{labels[listId - 1]}}</div>
                                <div v-if="viewSize.smDown" class="no-select pointer"
                                    :title="openContainers.includes(listId) ? 'Aufklappen' : 'Zuklappen'">
                                    <i class="material-icons" aria-hidden="true" role="img">
                                        {{openContainers.includes(listId) ? 'keyboard_arrow_down' : 'keyboard_arrow_up'}}
                                    </i>
                                </div>
                            </div>
                            <div :class="['pd-container-body', {'pd-container-closed': openContainers.includes(listId)}]">
                                <div v-if="listedWidgets[listId - 1].length === 0" class="full-width text-center">
                                    <i class="material-icons md-48" aria-hidden="true" role="img">dashboard</i>
                                    <br/>
                                    Hier können Sie sich Informationen aus dem Intranet zusammengefasst anzeigen lassen: Ihre digitale Übersicht einfach gemacht. Wählen Sie einfach "Bearbeiten" aus und aktivieren Sie die für Sie relevanten Informationen.
                                </div>
                                <template v-else>
                                    <widget-base-personal-dashboard-popup v-for="element of listedWidgets[listId - 1]" :widget="element" :content="element.content"
                                        :small="element.small"
                                        :draggable="false" :removable="false"
                                        :clickable="true"/>
                                </template>
                            </div>
                        </div>
                    </div>

                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
    import {usePortuna} from '@/composables/portuna';
    import axios from 'axios';
    import LoadingIndicator from '@/components/LoadingIndicator.vue';
    import PersonBoxUser from '@/components/PersonBoxUser.vue';
    import TopBarTimeTracker from '@/components/TopBarTimeTracker.vue';
    import WidgetBasePersonalDashboardPopup from '@/components/widgets/WidgetBasePersonalDashboardPopup.vue';
    import {SubmitIcons} from '@/library/design';
    import {isObject} from '@/library/utils';
    import {errorHandler, XHR_STATUS_DEFAULT} from '@/utils/portuna';
    import type {TinyEmitter} from 'tiny-emitter';
    import dateHelper from '../utils/portuna/dateHelper';
    import {useStore} from 'vuex';
    import {useRouter} from 'vue-router';
    import {ref, computed, onUnmounted, watch, inject} from 'vue';

    const popupEmitter = inject<TinyEmitter>('popup-emitter')!;

    const portuna     = usePortuna();
    const currentUser = portuna.currentUser;

    const props = withDefaults(
        defineProps<{
            afterLogin?: boolean;
        }>(),
        {
            afterLogin: false,
        },
    );

    const store = useStore();

    const viewSize = store.state.viewSize;

    const router = useRouter();

    const newsContainer = ref<HTMLDivElement>();

    const loading        = ref<boolean>(true);
    const globalNews     = ref<
        {
            id: number;
            title: string;
            content_teaser: string;
            preview: string;
            created: string;
            link: string;
            paths: Record<string, string>;
            imagePath?: string;
        }[]
    >([]);
    const userWidgets    = ref<IPersonalDashboardUserWidget[]>([]);
    const labels         = ref<string[]>([]);
    const openContainers = ref<number[]>([]);

    const layoutData = computed<LayoutData>(() => store.state.layoutData);

    const timeTrackerIsLoggedIn = computed<boolean>(() => store.getters['timeTracker/isLoggedIn']);

    const dontShowTimeTrackerInfos = computed(() =>
    {
        return currentUser.value.hasTimeTracker && timeTrackerIsLoggedIn.value;
    });

    const isPopupOpen = computed(() =>
    {
        return store.state.personalDashboard?.isOpen;
    });

    const listedWidgets = computed(() =>
    {
        return [
            filterEntries(userWidgets.value, 0),
            filterEntries(userWidgets.value, 1),
            filterEntries(userWidgets.value, 2),
        ];
    });

    const title = computed((): string =>
    {
        return (
            'Herzlich Willkommen ' +
            currentUser.value.name +
            '!' +
            (viewSize.mdDown || dontShowTimeTrackerInfos.value
                ? ''
                : ' Möchten Sie sich zur Arbeit anmelden?')
        );
    });

    onUnmounted((): void =>
    {
        store.commit('personalDashboard/setOpen', false);
    });

    const filterEntries = (
        widgets: IPersonalDashboardUserWidget[],
        listNumber: number = 0,
    ): IPersonalDashboardUserWidget[] =>
    {
        return widgets
        .filter((widget) => widget.list === listNumber)
        .sort((a, b) => a.position - b.position);
    };

    const scrollLeft = (): void =>
    {
        newsContainer.value!.scrollBy({
            left: -300,
            behavior: 'smooth',
        });
    };

    const scrollRight = (): void =>
    {
        newsContainer.value!.scrollBy({
            left: 300,
            behavior: 'smooth',
        });
    };

    const toggleContainer = (id: number): void =>
    {
        if (!viewSize.smDown)
        {
            return;
        }

        if (openContainers.value.includes(id))
        {
            openContainers.value = openContainers.value.filter((identifier) => identifier !== id);
        }
        else
        {
            openContainers.value.push(id);
        }
    };

    const fetchNews = (): Promise<void> =>
    {
        return new Promise<void>((resolve) =>
        {
            axios
            .get('/settings/personal-dashboard-popup/fetch-global-news')
            .then((response) =>
            {
                const {
                          data,
                          status,
                      } = response.data;

                if (status !== XHR_STATUS_DEFAULT)
                {
                    throw response;
                }

                globalNews.value = data;

                globalNews.value = globalNews.value.map((news) =>
                {
                    return {
                        ...news,
                        imagePath:
                            news.paths?.huge ??
                            news.paths?.large ??
                            news.paths?.medium ??
                            news.paths?.preview ??
                            null,
                    };
                });
            })
            .catch(errorHandler)
            .finally(() =>
            {
                resolve();
            });
        });
    };

    const fetchWidgets = (): Promise<void> =>
    {
        return new Promise<void>((resolve) =>
        {
            axios
            .get('/settings/personal-dashboard-popup/fetch/from-popup/true')
            .then((response) =>
            {
                const {
                          data,
                          status,
                      } = response.data;

                if (status !== XHR_STATUS_DEFAULT)
                {
                    throw response;
                }

                userWidgets.value = (
                    isObject(data.user_widgets) ? Object.values(data.user_widgets) : data.user_widgets
                ) as IPersonalDashboardUserWidget[];

                labels.value = data.labels;
            })
            .catch(errorHandler)
            .finally(() =>
            {
                resolve();
            });
        });
    };

    const setButtons = (withoutLoginInfo: boolean) =>
    {
        popupEmitter.emit('setButtons', [
            {
                type: 'default',
                title: withoutLoginInfo ? 'Ohne Anmeldung fortfahren' : 'Schließen',
                icon: SubmitIcons.close,
                cmd: () =>
                {
                    store.commit('personalDashboard/setOpen', false);

                    popupEmitter.emit('close');
                },
            },
            {
                title: 'Bearbeiten',
                type: 'secondary',
                icon: 'edit',
                cmd: () =>
                {
                    store.commit('personalDashboard/setOpen', false);

                    popupEmitter.emit('close');

                    router.push({
                        name: currentUser.value.admin
                            ? 'settings-personal-dashboard-popup'
                            : 'settings-user-personal-dashboard-popup',
                    });
                },
            },
        ] as PopupButton[]);
    };

    const fetch = (): void =>
    {
        loading.value = true;

        fetchNews().then(() =>
        {
            fetchWidgets().then(() =>
            {
                loading.value = false;

                popupEmitter.emit('lazy-load-focus');
            });
        });
    };

    store.commit('personalDashboard/setOpen', true);

    fetch();

    setButtons(props.afterLogin);

    store.commit('personalDashboard/setNeeds', false);

    watch(isPopupOpen, () =>
    {
        if (!isPopupOpen.value)
        {
            popupEmitter.emit('close');
        }
    });

    watch(dontShowTimeTrackerInfos, () =>
    {
        if (dontShowTimeTrackerInfos.value)
        {
            setButtons(false);
        }
    });

    defineExpose(Object.assign({}, portuna, {
        componentName: 'PersonalDashboardPopup',
    }));
</script>